/**
 * Progress model.
 *
 * The one non-obvious decision here is `updatedAt` on every *entry* rather than
 * on the snapshot. It costs nothing today and it is what makes a future cloud
 * sync a ~10-line last-writer-wins merge at item granularity: two devices that
 * tick different items never conflict. Retrofitting it later would force a data
 * migration, so it goes in from the start.
 */

export interface Profile {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  /** Accent tint for the profile chip. */
  color?: string;
}

export interface ProgressEntry {
  done: boolean;
  note?: string;
  /** Epoch ms. The unit of conflict resolution. */
  updatedAt: number;
}

/** itemId -> entry */
export type SetProgress = Record<string, ProgressEntry>;

export interface ProgressSnapshot {
  schemaVersion: 1;
  profileId: string;
  /** Monotonic local revision, bumped on every write. */
  rev: number;
  updatedAt: number;
  /**
   * setId -> itemId -> entry.
   * Entries for items no longer present in content are RETAINED, not pruned,
   * so that removing and later restoring an item does not lose a tick.
   */
  sets: Record<string, SetProgress>;
}

export interface ProgressPatch {
  setId: string;
  itemId: string;
  /** `null` deletes the entry outright. */
  entry: Partial<Omit<ProgressEntry, 'updatedAt'>> | null;
}

export interface BackupV1 {
  format: 'meowtracker-backup';
  version: 1;
  exportedAt: string;
  app: { version: string };
  profiles: Profile[];
  /** profileId -> snapshot */
  progress: Record<string, ProgressSnapshot>;
}

export function isBackupV1(value: unknown): value is BackupV1 {
  if (typeof value !== 'object' || value === null) return false;
  const b = value as Partial<BackupV1>;
  return (
    b.format === 'meowtracker-backup' &&
    b.version === 1 &&
    Array.isArray(b.profiles) &&
    typeof b.progress === 'object' &&
    b.progress !== null
  );
}

export function emptySnapshot(profileId: string): ProgressSnapshot {
  return {
    schemaVersion: 1,
    profileId,
    rev: 0,
    updatedAt: Date.now(),
    sets: {},
  };
}
